import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { BarChart2, TrendingUp, Users, BookOpen, Award, DollarSign, Star, Trophy, CheckCircle } from 'lucide-react';
import { adminAPI, judgingAPI } from '../lib/api';
import { LoadingPage, Modal } from '../components/shared';
import toast from 'react-hot-toast';

// ── ANALYTICS PAGE ─────────────────────────────────────────────────────────────
export function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminAPI.getAnalytics(), adminAPI.getDashboard()])
      .then(([analyticsRes, dashRes]) => {
        setData({ analytics: analyticsRes.data, dashboard: dashRes.data });
      })
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  const { analytics, dashboard } = data;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">Platform-wide statistics and insights</p>
        </div>
      </div>

      {/* KPI Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Total Users', value: dashboard.users?.total || 0, sub: `${dashboard.users?.pending || 0} pending`, icon: Users, color: 'var(--teal)' },
          { label: 'Research Topics', value: dashboard.topics?.total || 0, sub: `${dashboard.topics?.open || 0} open`, icon: BookOpen, color: 'var(--gold)' },
          { label: 'Total Funding', value: Number(parseFloat(dashboard.funding?.total_pledged || 0)).toLocaleString(), sub: 'pledged', icon: DollarSign, color: 'var(--success)' },
          { label: 'Applications', value: dashboard.applications?.total || 0, sub: `${dashboard.applications?.accepted || 0} accepted`, icon: Award, color: 'var(--info)' },
        ].map(kpi => (
          <div key={kpi.label} className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{kpi.label}</div>
              <kpi.icon size={18} style={{ color: kpi.color, opacity: 0.7 }} />
            </div>
            <div style={{ fontSize: '1.9rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{kpi.value}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* User breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 20 }}>User Breakdown</h3>
          {[
            { label: 'Students', value: dashboard.users?.students || 0, color: 'var(--teal)' },
            { label: 'Alumni', value: dashboard.users?.alumni || 0, color: 'var(--gold)' },
            { label: 'Lecturers', value: dashboard.users?.lecturers || 0, color: 'var(--info)' },
          ].map(({ label, value, color }) => {
            const total = (dashboard.users?.students || 0) + (dashboard.users?.alumni || 0) + (dashboard.users?.lecturers || 0);
            const pct = total ? Math.round((value / total) * 100) : 0;
            return (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                  <span style={{ color, fontWeight: 600 }}>{value} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct}%)</span></span>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.8s ease' }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 20 }}>Topics by Domain</h3>
          {analytics.topicsByDomain?.slice(0, 6).map((d, i) => {
            const maxCount = Math.max(...analytics.topicsByDomain.map(x => parseInt(x.count)));
            const pct = Math.round((parseInt(d.count) / maxCount) * 100);
            return (
              <div key={d.domain} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{d.domain}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{d.count} topics · {Number(parseFloat(d.funding || 0)).toLocaleString()}</span>
                </div>
                <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3 }}>
                  <div style={{
                    height: '100%', width: `${pct}%`,
                    background: `hsl(${(i * 47) % 360}, 60%, 55%)`,
                    borderRadius: 3, transition: 'width 0.8s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Applications status */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 20 }}>Application Status Distribution</h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {analytics.applicationsByStatus?.map(s => {
            const colors = { pending: 'var(--warning)', under_review: 'var(--info)', accepted: 'var(--success)', rejected: 'var(--error)', withdrawn: 'var(--text-muted)' };
            return (
              <div key={s.status} style={{
                padding: '16px 20px', background: 'rgba(255,255,255,0.03)',
                borderRadius: 10, border: 'var(--border-subtle)', flex: '1 1 140px', textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.6rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: colors[s.status] || 'var(--text-primary)' }}>
                  {s.count}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, textTransform: 'capitalize' }}>
                  {s.status.replace(/_/g, ' ')}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── RANKINGS PAGE ──────────────────────────────────────────────────────────────
export function RankingsPage() {
  const [rankings, setRankings] = useState([]);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [publishModal, setPublishModal] = useState(false);
  const [selected, setSelected] = useState({ finalists: [], winners: [] });

  useEffect(() => {
    judgingAPI.getRankings()
      .then(r => setRankings(r.data))
      .catch(() => toast.error('Failed to load rankings'))
      .finally(() => setLoading(false));
  }, []);

  const toggleFinalist = (id) => {
    setSelected(s => ({
      ...s,
      finalists: s.finalists.includes(id)
        ? s.finalists.filter(x => x !== id)
        : [...s.finalists, id]
    }));
  };

  const handlePublish = async () => {
    try {
      await adminAPI.publishResults({
        finalists: selected.finalists.map(id => ({ project_id: id })),
        winners: selected.winners.map((id, i) => ({ project_id: id, rank: i + 1 }))
      });
      toast.success('Results published!');
      setPublishModal(false);
      judgingAPI.getRankings().then(r => setRankings(r.data));
    } catch {
      toast.error('Failed to publish results');
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Project Rankings</h1>
          <p className="page-subtitle">Aggregated scores from all judges</p>
        </div>
        {user?.role === 'admin' && <button className="btn btn-primary" onClick={() => setPublishModal(true)}>
          <Trophy size={16} /> Publish Results
        </button>}
      </div>

      {rankings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>
          No scored projects yet. Assign judges and collect scores first.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rankings.map((project, idx) => (
            <div key={project.id} className="card" style={{
              padding: '20px 24px',
              border: project.is_winner ? '1px solid rgba(201,162,39,0.4)' : project.is_finalist ? '1px solid rgba(45,212,191,0.25)' : 'var(--border-subtle)',
              background: project.is_winner ? 'rgba(201,162,39,0.05)' : undefined
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                {/* Rank */}
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: idx === 0 ? 'var(--gold)' : idx === 1 ? 'rgba(192,192,192,0.3)' : idx === 2 ? 'rgba(205,127,50,0.3)' : 'rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: idx < 3 ? '1.1rem' : '0.9rem',
                  fontFamily: 'var(--font-display)', fontWeight: 700,
                  color: idx === 0 ? '#000' : 'var(--text-secondary)'
                }}>
                  {idx < 3 ? ['🥇','🥈','🥉'][idx] : `#${idx + 1}`}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600 }}>
                      {project.title}
                    </h3>
                    {project.is_winner && <span style={{ fontSize: '0.7rem', color: 'var(--gold)', background: 'rgba(201,162,39,0.15)', padding: '2px 8px', borderRadius: 4 }}>WINNER</span>}
                    {project.is_finalist && !project.is_winner && <span style={{ fontSize: '0.7rem', color: 'var(--teal)', background: 'rgba(45,212,191,0.1)', padding: '2px 8px', borderRadius: 4 }}>FINALIST</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
                    <span>{project.student_name}</span>
                    {project.lecturer_name && <span>· {project.lecturer_name}</span>}
                    {project.domain && <span style={{ color: 'var(--teal)' }}>· {project.domain}</span>}
                    <span>· {project.judge_count} judge{project.judge_count !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Score breakdown */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {[
                      { label: 'ORI', value: project.avg_originality },
                      { label: 'TEC', value: project.avg_technical_merit },
                      { label: 'FEA', value: project.avg_feasibility },
                      { label: 'IMP', value: project.avg_impact },
                      { label: 'PRE', value: project.avg_presentation },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                        <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
                          {value ? parseFloat(value).toFixed(1) : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    padding: '8px 16px',
                    background: 'rgba(201,162,39,0.1)',
                    borderRadius: 8,
                    textAlign: 'center',
                    minWidth: 72
                  }}>
                    <div style={{ fontSize: '1.4rem', fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--gold)' }}>
                      {project.final_score ? parseFloat(project.final_score).toFixed(2) : '—'}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>/ 10.00</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Publish Modal */}
      <Modal open={publishModal} onClose={() => setPublishModal(false)} title="Publish Results" size="lg">
        <div className="modal-body">
          <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: '0.875rem' }}>
            Select finalists and winners to publish. This will make results visible on the public website.
          </p>
          <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rankings.map((p, i) => (
              <div key={p.id} style={{
                padding: '12px 16px', background: 'rgba(255,255,255,0.03)',
                borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12,
                border: selected.finalists.includes(p.id) ? '1px solid var(--teal)' : 'var(--border-subtle)'
              }}>
                <input
                  type="checkbox"
                  checked={selected.finalists.includes(p.id)}
                  onChange={() => toggleFinalist(p.id)}
                  style={{ accentColor: 'var(--teal)', width: 16, height: 16 }}
                />
                <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  #{i + 1} {p.title}
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--gold)', fontWeight: 600 }}>
                  {p.final_score ? parseFloat(p.final_score).toFixed(2) : '—'}
                </span>
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setPublishModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handlePublish}>
              <Trophy size={15} /> Publish Selected as Finalists
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
